import { useState, useCallback, useEffect, useMemo } from "react";
import { isAxiosError } from "axios";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
} from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { VideoPreview } from "../../../shared/interfaces/VideoPreview";
import { EvidenceFileDDB } from "../../../shared/interfaces/EvidenceFileDDB";
import { EvidenceListResponse } from "../../../shared/interfaces/EvidenceListResponse";
import { VideoStatus } from "../../../shared/interfaces/VideoData";
import { useToast } from "../../../shared/toast";
import { isPlausibleRecordedAtMs } from "../utils/fileMetadata";
import { queryClient } from "../../../shared/api/queryClient";
import {
  DEFAULT_EVIDENCE_SORT,
  evidenceKeys,
  evidenceListInfiniteQuery,
  fetchEvidencePage,
  mergePollResults,
  nextEvidenceSort,
  trimToFirstPage,
  type EvidenceListFilters,
  type EvidenceSort,
  type EvidenceSortField,
} from "../../../features/evidence/api/evidenceQueries";

/** Which slice of evidence the page is showing: ingested videos or AI-filtered
 *  (rejected / never-fetched) candidates. Maps to the `videoState` query param. */
export type EvidenceTab = "ingested" | "unfetched";

export const getDisplayRecordedAtMs = (evidence: EvidenceFileDDB): number =>
  isPlausibleRecordedAtMs(evidence.recordedAt)
    ? evidence.recordedAt
    : evidence.createdAt;

const isVideoStatus = (status: string): status is VideoStatus =>
  (Object.values(VideoStatus) as string[]).includes(status);

/** Validates a backend status string against the VideoStatus enum. An unknown
 *  value is passed through (not coerced to a default) so the status chip still
 *  surfaces what the backend actually sent; downstream switches handle it via
 *  their default branches. */
export const parseVideoStatus = (status: string): VideoStatus => {
  if (!isVideoStatus(status)) {
    console.warn(`Unknown evidence status "${status}"`);
  }
  return status as VideoStatus;
};

// Helper to map API response to VideoPreview
export const mapEvidenceToVideoPreview = (
  evidence: EvidenceFileDDB,
): VideoPreview => {
  const status = parseVideoStatus(evidence.status);
  const isFiltered = status === VideoStatus.NOT_EVIDENCE;
  return {
    id: evidence.evidenceId,
    thumbnailUrl: evidence.file, // Using file URL as thumbnail (empty when filtered)
    rmsName: evidence.externalId || "N/A",
    // Filtered candidates were never fetched, so they carry no filename yet.
    fileName:
      evidence.fileName || (isFiltered ? "Filtered footage (not fetched)" : ""),
    recordedDate: new Date(getDisplayRecordedAtMs(evidence)).toString(),
    uploadedDate: new Date(evidence.createdAt).toString(),
    officerName: evidence.uploadedByUserId || "System",
    duration: evidence.duration,
    status,
    device: evidence.device || "Bodycam",
    fileType: evidence.fileType,
    judgeReasons: evidence.judgeReasons,
    judgeTier: evidence.judgeTier,
    judgeConfidence: evidence.judgeConfidence,
  };
};

/**
 * Evidence list on react-query. The external API is identical to the old
 * hand-rolled implementation:
 * - filter/sort/search/tab changes "replace" (new query key; previous list
 *   shown via placeholderData while the new one loads — the VideoGrid is keyed
 *   by tab, so it remounts and shows skeletons over a tab switch),
 * - loadMore "appends" via the infinite query cursor,
 * - a separate 10s page-1 poll "merges": rows refresh in place, new items
 *   prepend, the pagination cursor survives.
 *
 * `tab` selects which slice the backend returns (ingested vs AI-rejected
 * candidates). Search/date filters are disabled on the unfetched tab — its
 * filter controls are locked, so applying them would silently filter a list
 * the user can't see the controls for. Sorting stays active on both tabs.
 */
export const useListVideos = (tab: EvidenceTab = "ingested") => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [bodycamVendor, setBodycamVendor] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // States for date range filter
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);

  // State for sorting: which date field + direction. Mirrors the report list
  // (sort field + asc/desc toggle), defaulting to newest-first on upload date.
  const [sort, setSort] = useState<EvidenceSort>(DEFAULT_EVIDENCE_SORT);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toast = useToast();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Filters disabled on the unfetched tab (its controls are locked).
  const filtersApply = tab === "ingested";
  const filters = useMemo<EvidenceListFilters>(
    () => ({
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
      videoState: tab === "unfetched" ? "rejected" : "ingested",
      ...(filtersApply && dateFrom
        ? { dateFrom: dateFrom.startOf("day").toISOString() }
        : {}),
      ...(filtersApply && dateTo
        ? { dateTo: dateTo.endOf("day").toISOString() }
        : {}),
      ...(filtersApply && debouncedSearchTerm
        ? { textQuery: debouncedSearchTerm }
        : {}),
    }),
    [sort, tab, filtersApply, dateFrom, dateTo, debouncedSearchTerm],
  );

  const listQuery = useInfiniteQuery({
    ...evidenceListInfiniteQuery(filters),
    placeholderData: keepPreviousData,
    // The old unconditional 10s interval self-healed a failed load; with the
    // poll gated on isSuccess, an errored list must retry itself instead.
    refetchInterval: (query) =>
      query.state.status === "error" ? 10_000 : false,
  });

  const videos = useMemo<VideoPreview[]>(
    () =>
      listQuery.data?.pages.flatMap((p) =>
        (p.evidence ?? []).map(mapEvidenceToVideoPreview),
      ) ?? [],
    [listQuery.data],
  );

  // Poll every 10 seconds with a page-1 request whose results merge into the
  // loaded pages (rows refresh in place, new items prepended, cursor preserved).
  const pollQuery = useQuery({
    queryKey: evidenceKeys.poll(filters),
    queryFn: ({ signal }) => fetchEvidencePage(filters, undefined, signal),
    enabled: listQuery.isSuccess,
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    const fresh = pollQuery.data;
    // Skip while a next page is in flight: the infinite query snapshots its
    // pages at fetch start and writes snapshot+newPage on resolve, which would
    // silently discard a merge landing in between. Re-applies on settle.
    if (!fresh || listQuery.isFetchingNextPage) return;
    queryClient.setQueryData<
      InfiniteData<EvidenceListResponse, string | undefined>
    >(evidenceKeys.list(filters), (old) =>
      old
        ? mergePollResults(old, fresh, {
            dropMissingFetching: filters.videoState === "rejected",
          })
        : old,
    );
    // exhaustive-deps is disabled here (it can't be satisfied without breaking
    // correctness), for two reasons:
    // (1) We key on pollQuery.dataUpdatedAt, NOT pollQuery.data — react-query's
    //     structural sharing keeps `data` referentially stable across a
    //     deep-equal refetch, so a data-keyed effect wouldn't re-run; the
    //     timestamp is the real "a fetch landed" signal.
    // (2) `filters` is read via closure rather than listed: changing it re-keys
    //     pollQuery, which resets dataUpdatedAt (a new key starts at 0), so the
    //     effect already re-runs with a fresh closure on every filters change —
    //     and no-ops until that key's first poll resolves. Listing it would
    //     only add redundant no-op runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollQuery.dataUpdatedAt, listQuery.isFetchingNextPage]);

  // bodycamVendor is sticky: keep the latest non-null value from any response.
  useEffect(() => {
    const pages = listQuery.data?.pages ?? [];
    for (let i = pages.length - 1; i >= 0; i--) {
      const vendor = pages[i].bodycamVendor;
      if (vendor) {
        setBodycamVendor(vendor);
        return;
      }
    }
  }, [listQuery.data]);
  useEffect(() => {
    if (pollQuery.data?.bodycamVendor) {
      setBodycamVendor(pollQuery.data.bodycamVendor);
    }
  }, [pollQuery.data]);

  // isInitialLoad latches false after the first settle and never re-trues; the
  // VideoGrid is keyed by tab, so it remounts and shows skeletons over a tab
  // switch without the hook needing to reset this.
  useEffect(() => {
    if (isInitialLoad && (listQuery.isSuccess || listQuery.isError)) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, listQuery.isSuccess, listQuery.isError]);

  // Surface fetch failures exactly as before.
  useEffect(() => {
    const error = listQuery.error || pollQuery.error;
    if (!error) return;
    const message = isAxiosError(error) ? error.response?.data?.error : null;
    toast.error(message || "Failed to fetch videos");
  }, [listQuery.error, pollQuery.error, toast]);

  const isLoading = listQuery.isFetching || pollQuery.isFetching;
  const hasMore = !!listQuery.hasNextPage;

  const { fetchNextPage } = listQuery;
  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      void fetchNextPage();
    }
  }, [hasMore, isLoading, fetchNextPage]);

  /**
   * Optimistically flips a video to FETCHING after its fetch was requested, so
   * the card stays visible and shows the in-progress state immediately. Writes
   * straight to the list cache; the 10-second merge poll reconciles the real
   * status once the backend catches up.
   */
  const markVideoFetching = useCallback(
    (videoId: string) => {
      queryClient.setQueryData<
        InfiniteData<EvidenceListResponse, string | undefined>
      >(evidenceKeys.list(filters), (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                evidence: (page.evidence ?? []).map((e) =>
                  e.evidenceId === videoId
                    ? { ...e, status: VideoStatus.FETCHING }
                    : e,
                ),
              })),
            }
          : old,
      );
    },
    [filters],
  );

  // Full "replace": drop appended pages and refetch page 1 (post-upload
  // refresh and the SelectionControls refresh button). Trim-then-invalidate
  // instead of resetQueries: reset clears data to undefined, blanking the
  // grid for the round trip; trimming keeps page-1 rows visible and the
  // invalidate refetches exactly one page.
  const refreshVideos = useCallback(() => {
    queryClient.setQueryData<InfiniteData<EvidenceListResponse, string | undefined>>(
      evidenceKeys.list(filters),
      (old) => (old ? trimToFirstPage(old) : old),
    );
    void queryClient.invalidateQueries({ queryKey: evidenceKeys.list(filters) });
  }, [filters]);

  // Clicking a date chip: re-click flips direction, a new field starts
  // newest-first — same toggle the report list's sort chips use.
  const requestSort = useCallback((field: EvidenceSortField) => {
    setSort((current) => nextEvidenceSort(current, field));
  }, []);

  const resetSort = useCallback(() => setSort(DEFAULT_EVIDENCE_SORT), []);

  return {
    videos,
    isLoading,
    isInitialLoad,
    bodycamVendor,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sortBy: sort.sortBy,
    sortOrder: sort.sortOrder,
    requestSort,
    resetSort,
    fetchVideos: refreshVideos,
    markVideoFetching,
    loadMore,
    hasMore,
    searchTerm,
    setSearchTerm,
    handleSearchChange,
  };
};
